import * as React from "react";
import type { FlowDefinition } from "./types";
import type { NodeRunMeta } from "./FlowRunView";
import { parseTurnBudget } from "./RunDebugger";

/**
 * Run graphs (flow-debugger-ui W3) — hand-rolled SVG over data already captured.
 *
 * No new collection and no chart library: the webview CSP is `default-src 'none'` with nonce-only
 * scripts, so a CDN chart would fail silently, and every number here already exists on
 * `NodeRunMeta` (tokens, duration, turns) or in the flow definition (declared budgets).
 *
 * The one that matters most is **cumulative tokens**: the MarketPulse v5 run burned 76.4M tokens
 * across 13 nodes with nothing on screen rising while it happened. A per-node bar chart would not
 * have shown it either — the run looked like thirteen ordinary nodes. A cumulative line does,
 * because the slope is the story.
 *
 * A node with no measurement is OMITTED rather than plotted as zero: a flat bar reads as "this node
 * was cheap", which is the opposite of "we do not know".
 */

export type RunGraphKind = "tokens" | "cumulative" | "duration" | "turns" | "payload";

export interface RunSeriesPoint {
    nodeId: string;
    label: string;
    /** Total tokens for the node, when measured. */
    tokens?: number;
    /** Running total across the run, in execution order. */
    cumulative?: number;
    durationMs?: number;
    turnsUsed?: number;
    turnSoft?: number;
    turnHard?: number;
    /** Characters carried INTO the node's prompt (flow-data-plane W5). */
    promptChars?: number;
    /** Bytes this node's artifacts hold on disk — payload that did NOT travel in the prompt. */
    artifactBytes?: number;
}

/**
 * Order nodes by when they actually STARTED, not by their position in the definition — a run that
 * looped or jumped executed in an order the graph has to reflect, or the cumulative line is fiction.
 */
export function buildRunSeries(
    flow: FlowDefinition,
    metas: Record<string, NodeRunMeta>
): RunSeriesPoint[] {
    const defs = new Map((flow.nodes ?? []).map((n) => [n.nodeId, n]));
    const entries = Object.entries(metas)
        .filter(([, m]) => !!m)
        .sort((a, b) => (a[1].startedAt ?? 0) - (b[1].startedAt ?? 0));

    let running = 0;
    return entries.map(([nodeId, meta]) => {
        const def = defs.get(nodeId);
        const budget = parseTurnBudget(def?.turnBudget);
        const tokens = meta.totalTokens;
        if (tokens != null) {
            running += tokens;
        }
        return {
            nodeId,
            label: def?.name || nodeId,
            tokens,
            cumulative: tokens != null ? running : undefined,
            durationMs: meta.durationMs,
            turnsUsed: meta.turnsUsed,
            turnSoft: meta.turnBudgetSoft ?? budget?.soft,
            turnHard: meta.turnBudgetHard ?? budget?.hard,
            promptChars: meta.promptChars,
            artifactBytes: meta.artifactBytes,
        };
    });
}

const W = 460;
const H = 120;
const PAD_L = 40;
const PAD_R = 8;
const PAD_T = 10;
const PAD_B = 26;

function niceMax(v: number): number {
    if (v <= 0) return 1;
    const mag = Math.pow(10, Math.floor(Math.log10(v)));
    const n = v / mag;
    const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
    return step * mag;
}

function fmtCompact(v: number): string {
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(v >= 10_000 ? 0 : 1)}k`;
    return String(Math.round(v));
}

/** Bytes, compactly. Sizes here span three orders of magnitude within one run. */
export function fmtBytes(v: number): string {
    if (v >= 1024 * 1024) return `${(v / (1024 * 1024)).toFixed(1)}MB`;
    if (v >= 1024) return `${(v / 1024).toFixed(v >= 10240 ? 0 : 1)}KB`;
    return `${Math.round(v)}B`;
}

function fmtMs(ms: number): string {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    const s = ms / 1000;
    if (s < 90) return `${s.toFixed(1)}s`;
    return `${Math.floor(s / 60)}m${Math.round(s % 60)}s`;
}

/** Short x-axis tick — node labels are long and the axis is narrow. */
function tick(label: string): string {
    return label.length > 9 ? `${label.slice(0, 8)}…` : label;
}

function Empty({ what }: { what: string }) {
    return (
        <div className="fn-run-graph-empty">
            No {what} measured yet — nothing is plotted rather than plotting zeros.
        </div>
    );
}

function Axis({ max, unit }: { max: number; unit: (v: number) => string }) {
    return (
        <>
            {[0, 0.5, 1].map((f) => {
                const y = PAD_T + (H - PAD_T - PAD_B) * (1 - f);
                return (
                    <g key={f}>
                        <line
                            x1={PAD_L}
                            x2={W - PAD_R}
                            y1={y}
                            y2={y}
                            className="fn-run-graph-grid"
                        />
                        <text x={PAD_L - 4} y={y + 3} textAnchor="end" className="fn-run-graph-axis">
                            {unit(max * f)}
                        </text>
                    </g>
                );
            })}
        </>
    );
}

function BarChart({
    points,
    value,
    unit,
    title,
    tone,
    budgetOf,
}: {
    points: RunSeriesPoint[];
    value: (p: RunSeriesPoint) => number | undefined;
    unit: (v: number) => string;
    title: string;
    tone: string;
    /** Optional per-bar budget line (turns vs declared budget). */
    budgetOf?: (p: RunSeriesPoint) => number | undefined;
}) {
    const measured = points.filter((p) => value(p) != null);
    if (measured.length === 0) return <Empty what={title.toLowerCase()} />;

    const rawMax = Math.max(
        ...measured.map((p) => value(p) ?? 0),
        ...(budgetOf ? measured.map((p) => budgetOf(p) ?? 0) : [0])
    );
    const max = niceMax(rawMax);
    const plotH = H - PAD_T - PAD_B;
    const slot = (W - PAD_L - PAD_R) / measured.length;
    const barW = Math.max(3, Math.min(28, slot * 0.62));

    return (
        <svg
            className="fn-run-graph-svg"
            viewBox={`0 0 ${W} ${H}`}
            role="img"
            aria-label={`${title} per node`}
            preserveAspectRatio="none"
        >
            <Axis max={max} unit={unit} />
            {measured.map((p, i) => {
                const v = value(p) ?? 0;
                const h = max > 0 ? (v / max) * plotH : 0;
                const x = PAD_L + slot * i + (slot - barW) / 2;
                const y = PAD_T + plotH - h;
                const budget = budgetOf?.(p);
                const over = budget != null && v > budget;
                return (
                    <g key={p.nodeId}>
                        <rect
                            x={x}
                            y={y}
                            width={barW}
                            height={Math.max(1, h)}
                            className={`fn-run-graph-bar${over ? " over" : ""}`}
                            style={over ? undefined : { fill: tone }}
                        >
                            <title>{`${p.label}: ${unit(v)}${budget != null ? ` (budget ${unit(budget)})` : ""}`}</title>
                        </rect>
                        {budget != null ? (
                            <line
                                x1={x - 2}
                                x2={x + barW + 2}
                                y1={PAD_T + plotH - (budget / max) * plotH}
                                y2={PAD_T + plotH - (budget / max) * plotH}
                                className="fn-run-graph-budget"
                            >
                                <title>{`${p.label}: budget ${unit(budget)}`}</title>
                            </line>
                        ) : null}
                        <text
                            x={PAD_L + slot * i + slot / 2}
                            y={H - PAD_B + 14}
                            textAnchor="middle"
                            className="fn-run-graph-axis"
                        >
                            {tick(p.label)}
                        </text>
                    </g>
                );
            })}
        </svg>
    );
}

function CumulativeChart({ points }: { points: RunSeriesPoint[] }) {
    const measured = points.filter((p) => p.cumulative != null);
    if (measured.length === 0) return <Empty what="tokens" />;

    const max = niceMax(measured[measured.length - 1].cumulative ?? 0);
    const plotH = H - PAD_T - PAD_B;
    const plotW = W - PAD_L - PAD_R;
    const stepX = measured.length > 1 ? plotW / (measured.length - 1) : 0;
    const xy = measured.map((p, i) => {
        const x = PAD_L + (measured.length > 1 ? stepX * i : plotW / 2);
        const y = PAD_T + plotH - ((p.cumulative ?? 0) / max) * plotH;
        return { x, y, p };
    });
    const path = xy.map((q, i) => `${i === 0 ? "M" : "L"}${q.x.toFixed(1)},${q.y.toFixed(1)}`).join(" ");
    const area = `${path} L${xy[xy.length - 1].x.toFixed(1)},${PAD_T + plotH} L${xy[0].x.toFixed(1)},${PAD_T + plotH} Z`;

    return (
        <svg
            className="fn-run-graph-svg"
            viewBox={`0 0 ${W} ${H}`}
            role="img"
            aria-label="Cumulative tokens over the run"
            preserveAspectRatio="none"
        >
            <Axis max={max} unit={fmtCompact} />
            <path d={area} className="fn-run-graph-area" />
            <path d={path} className="fn-run-graph-line" />
            {xy.map((q) => (
                <circle key={q.p.nodeId} cx={q.x} cy={q.y} r={2.5} className="fn-run-graph-dot">
                    <title>{`${q.p.label}: ${fmtCompact(q.p.cumulative ?? 0)} cumulative`}</title>
                </circle>
            ))}
            {xy.map((q, i) =>
                i === 0 || i === xy.length - 1 || xy.length <= 6 ? (
                    <text
                        key={`t-${q.p.nodeId}`}
                        x={q.x}
                        y={H - PAD_B + 14}
                        textAnchor={i === 0 ? "start" : i === xy.length - 1 ? "end" : "middle"}
                        className="fn-run-graph-axis"
                    >
                        {tick(q.p.label)}
                    </text>
                ) : null
            )}
        </svg>
    );
}

/**
 * Payload cost (flow-data-plane W5): what each node CARRIED in its prompt against what its artifacts
 * HOLD on disk.
 *
 * This is the picture of the whole program. Before the data plane, a shared payload is re-sent to
 * every consuming node, so the carried bars climb with the dataset and there are no held bars at all.
 * After it, the carried bars go flat — a handle is ~400 characters however large the payload — and
 * the size moves into the held bars, where it is paid for once.
 *
 * Both series are measured (prompt length as the node received it; artifact bytes as written). A node
 * with neither is omitted rather than drawn as a pair of zeros.
 */
function PayloadChart({ points }: { points: RunSeriesPoint[] }) {
    const pts = points.filter(
        (p) => (p.promptChars ?? 0) > 0 || (p.artifactBytes ?? 0) > 0
    );
    if (pts.length === 0) return <Empty what="payload" />;

    const max = niceMax(
        Math.max(...pts.map((p) => Math.max(p.promptChars ?? 0, p.artifactBytes ?? 0)))
    );
    const plotW = W - PAD_L - PAD_R;
    const plotH = H - PAD_T - PAD_B;
    const slot = plotW / pts.length;
    const bw = Math.max(2, Math.min(14, slot / 2 - 2));

    return (
        <svg
            viewBox={`0 0 ${W} ${H}`}
            className="fn-run-graph-svg"
            role="img"
            aria-label={`Payload carried in prompts versus held in artifacts, ${pts.length} nodes`}
        >
            <Axis max={max} unit={fmtBytes} />
            {pts.map((p, i) => {
                const x = PAD_L + slot * i + slot / 2;
                const carried = p.promptChars ?? 0;
                const held = p.artifactBytes ?? 0;
                const hc = (carried / max) * plotH;
                const hh = (held / max) * plotH;
                return (
                    <g key={p.nodeId}>
                        <rect
                            x={x - bw - 1}
                            y={PAD_T + plotH - hc}
                            width={bw}
                            height={Math.max(carried > 0 ? 1 : 0, hc)}
                            fill="var(--vscode-charts-red, #f14c4c)"
                        >
                            <title>{`${p.label}\ncarried in prompt: ${fmtBytes(carried)}`}</title>
                        </rect>
                        <rect
                            x={x + 1}
                            y={PAD_T + plotH - hh}
                            width={bw}
                            height={Math.max(held > 0 ? 1 : 0, hh)}
                            fill="var(--vscode-charts-green, #89d185)"
                        >
                            <title>{`${p.label}\nheld in artifacts: ${fmtBytes(held)}`}</title>
                        </rect>
                    </g>
                );
            })}
            {pts.map((p, i) =>
                i === 0 || i === pts.length - 1 || pts.length <= 6 ? (
                    <text
                        key={`t-${p.nodeId}`}
                        x={PAD_L + slot * i + slot / 2}
                        y={H - PAD_B + 14}
                        textAnchor={i === 0 ? "start" : i === pts.length - 1 ? "end" : "middle"}
                        className="fn-run-graph-axis"
                    >
                        {tick(p.label)}
                    </text>
                ) : null
            )}
        </svg>
    );
}

const GRAPH_TITLES: Record<RunGraphKind, string> = {
    tokens: "Tokens per node",
    cumulative: "Cumulative tokens",
    duration: "Duration per node",
    turns: "Turns vs budget",
    payload: "Payload: carried vs held",
};

/**
 * The graph stack. `show` selects which graphs render (the `docit.flowDebugger.graphs` setting);
 * omitting it renders all four.
 */
export function RunGraphs({
    flow,
    metas,
    show,
}: {
    flow: FlowDefinition;
    metas: Record<string, NodeRunMeta>;
    show?: ReadonlyArray<RunGraphKind>;
}) {
    const series = React.useMemo(() => buildRunSeries(flow, metas), [flow, metas]);
    const kinds: RunGraphKind[] = (show && show.length
        ? show
        : ["tokens", "cumulative", "duration", "turns", "payload"]) as RunGraphKind[];

    if (series.length === 0) {
        return (
            <div className="fn-run-graphs">
                <Empty what="node data" />
            </div>
        );
    }

    return (
        <div className="fn-run-graphs">
            {kinds.map((kind) => (
                <section key={kind} className="fn-run-graph">
                    <h4 className="fn-run-graph-title">{GRAPH_TITLES[kind]}</h4>
                    {kind === "tokens" ? (
                        <BarChart
                            points={series}
                            value={(p) => p.tokens}
                            unit={fmtCompact}
                            title="Tokens"
                            tone="var(--vscode-charts-blue, #3794ff)"
                        />
                    ) : kind === "cumulative" ? (
                        <CumulativeChart points={series} />
                    ) : kind === "payload" ? (
                        <PayloadChart points={series} />
                    ) : kind === "duration" ? (
                        <BarChart
                            points={series}
                            value={(p) => p.durationMs}
                            unit={fmtMs}
                            title="Duration"
                            tone="var(--vscode-charts-purple, #b180d7)"
                        />
                    ) : (
                        <BarChart
                            points={series}
                            value={(p) => p.turnsUsed}
                            unit={(v) => String(Math.round(v))}
                            title="Turns"
                            tone="var(--vscode-charts-green, #89d185)"
                            budgetOf={(p) => p.turnHard ?? p.turnSoft}
                        />
                    )}
                </section>
            ))}
        </div>
    );
}
